import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../../components/context/UserContext";
import getClient from "../../helpers/get-client";
import useMyPayments from "../../hooks/useMyPayments";
import WeeklyUsagePill from "../../components/WeeklyUsagePill";
import VerificationStatusBadge from "./VerificationStatusBadge";
import ComunicacionesPanel from "./ComunicacionesPanel";
import { EliminarCuentaZona } from "./EliminarCuentaDialog";
import { PROFESSIONS } from "../../helpers/profesiones";
import { pideVerificacion } from "../../helpers/pasos-verificacion";
import CheckoutDialog from "../../components/checkout/CheckoutDialog";
import JornadasAlMes from "../../components/checkout/JornadasAlMes";
import SaldoSueltas, { SubtituloSinPlan } from "../../components/suelta/SaldoSueltas"; // #1887
import { botonGhostSx, botonPrimarioSx } from "../../components/checkout/botonesSx";
import { formatoUsd } from "../../helpers/checkout-formato";
import { montoBrutoSugerido, montoIvaSugerido } from "../../helpers/billing-monto";
import { SOPORTE, WHATSAPP_SOPORTE } from "../../helpers/soporte";
import { modulosEncendidos } from "../../components/navbars/herramientas";
import { postCancelarSuscripcion } from "../../helpers/comunicaciones-api";

export const DirectoryPreview = ({ onNavigate }) => {
  const [colleagues, setColleagues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const c = getClient();
    c.get("/api/user/directory/")
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : r.data?.results || [];
        setColleagues(data.slice(0, 3));
      })
      .catch(() => setColleagues([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Typography variant="caption" sx={{ color: "rgba(var(--fg-rgb),0.5)" }}>
        Cargando...
      </Typography>
    );
  }

  if (colleagues.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "rgba(var(--fg-rgb),0.55)" }}>
        Todavía no hay otros colegas verificados.
      </Typography>
    );
  }

  // useFlexGap: sin él, `spacing` le pisa el margen a cada fila desde la
  // segunda y anula su `mx: -1`, así que la segunda quedaba 8 px corrida.
  return (
    <Stack spacing={1.25} useFlexGap>
      {colleagues.map((c) => {
        const initials = (c.full_name || c.username || "?")
          .trim()
          .split(/\s+/)
          .slice(0, 2)
          .map((p) => p[0]?.toUpperCase())
          .join("");
        const professionDef = PROFESSIONS.find((p) => p.value === c.profession);
        return (
          <Stack
            key={c.user_id}
            direction="row"
            spacing={1.25}
            alignItems="center"
            onClick={() => onNavigate(`/u/${c.user_id}`)}
            sx={{
              cursor: "pointer",
              borderRadius: 1.5,
              px: 1,
              py: 0.5,
              mx: -1,
              "&:hover": { backgroundColor: "rgba(var(--fg-rgb),0.04)" },
            }}
          >
            <Avatar
              src={c.profile_photo || undefined}
              sx={{
                width: 34,
                height: 34,
                fontSize: 12,
                fontWeight: 700,
                background: "linear-gradient(135deg, var(--color-primary), var(--color-primary-soft))",
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                noWrap
              >
                {c.full_name || c.username}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(var(--fg-rgb),0.55)", fontSize: "0.7rem" }}
              >
                {professionDef?.label || "—"}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

const client = getClient();

const SUSCRIPCION_PLAN_LABELS = {
  turno: "Turno",
  pro: "Turno Pro",
  max: "Turno Max",
  max_plus: "Turno Max+",
  clinica: "Clínica",
};

// Versión corta para la línea de detalle del panel (#1487): el mockup B la dice
// en una frase, no en la etiqueta larga de la tarjeta anterior.
const SUSCRIPCION_COHORTE_CORTA = {
  fundador: "cohorte fundadora",
  lista: "precio de lista",
  legacy: "tarifa acordada",
};

const formatoFechaLarga = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString("es-CL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const formatoMontoConMoneda = (monto, moneda) => {
  if (monto === null || monto === undefined) return "—";
  const cifra = Number(monto).toLocaleString("es-CL", { maximumFractionDigits: 2 });
  return moneda === "USD" ? `USD ${cifra}` : `$${cifra}`;
};

// "Mi suscripción" (Planner #1487). Mockup elegido: B, "el número manda"
// (tools/mockups/smartdx_mockups_mi_suscripcion.html). El bloque protagonista
// es SIEMPRE el mismo slot y solo cambia el dato que muestra: los días de la
// prueba, el monto mensual o la palabra "Permanente". No son tres maquetas.
//
// Cuál de los tres sale lo decide la cascada de siempre: primero `suscripcion`
// (si existe manda ella), después `access.estado === "demo"` con su contador, y
// todo lo demás (legacy, exenta, o `access` ausente por contrato viejo) es
// "sin fecha", que NO es un error ni pide hacer nada. `demo_vencida` y
// `vencida` no necesitan estado acá: SuscripcionGate ya bloqueó la app antes
// de que el médico llegue al perfil.
//
// El medidor de uso informa y nunca corta (#1247 R7/R8): ningún estado de este
// panel usa el token de peligro, ni la prueba por vencer, que llega a ámbar.

const MESES_LARGOS = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
const MESES_CORTOS = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

// `YYYY-MM-DD` (o un ISO completo) -> [año, mes0, día] sin pasar por Date, para
// que un periodo guardado como fecha no se corra un día por la zona horaria.
const partesDeFecha = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? [Number(m[1]), Number(m[2]) - 1, Number(m[3])] : null;
};

const periodoLegible = (iso) => {
  const p = partesDeFecha(iso);
  if (!p) return "";
  const mes = MESES_LARGOS[p[1]];
  return `${mes.charAt(0).toUpperCase()}${mes.slice(1)} ${p[0]}`;
};

const diaMesCorto = (iso) => {
  const p = partesDeFecha(iso);
  return p ? `${p[2]} ${MESES_CORTOS[p[1]]}` : "";
};

// `trial_ends_at` es un instante (datetime), no una fecha: se muestra en la
// zona del navegador, que es el día que el médico ve en su calendario.
const fechaLargaDeInstante = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" });
};

// Día siguiente al corte, como fecha corta. Aritmética con Date.UTC: no hay DST.
const diaSiguienteCorto = (iso) => {
  const p = partesDeFecha(iso);
  if (!p) return "";
  const d = new Date(Date.UTC(p[0], p[1], p[2] + 1));
  return `${d.getUTCDate()} ${MESES_CORTOS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

const capitalizar = (s) => (s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : "");

// Por debajo de este número de días la prueba pasa de azul a ámbar.
const DIAS_PRUEBA_POR_VENCER = 3;

const TONOS_PILDORA = {
  info: "--color-info",
  warn: "--color-warning",
  ok: "--color-success",
};

const PildoraEstado = ({ tono, children }) => (
  <Box
    component="span"
    data-testid="mi-suscripcion-estado"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      height: 23,
      px: 1.25,
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      whiteSpace: "nowrap",
      color: `var(${TONOS_PILDORA[tono]})`,
      bgcolor: `rgba(var(${TONOS_PILDORA[tono]}-rgb),0.12)`,
      border: `1px solid rgba(var(${TONOS_PILDORA[tono]}-rgb),0.25)`,
    }}
  >
    <Box component="span" sx={{ width: 7, height: 7, borderRadius: 999, bgcolor: "currentColor" }} />
    {children}
  </Box>
);

// El dato grande. Mismo slot para los tres: un entero chico, un monto con
// separadores de miles o una palabra. `minWidth: 0` + `overflowWrap` para que
// a 390 px un monto largo parta en vez de desbordar el panel.
const DatoGrande = ({ children, color, palabra = false, ...rest }) => (
  <Typography
    component="span"
    {...rest}
    sx={{
      fontSize: palabra ? { xs: 26, sm: 34 } : { xs: 34, sm: 44 },
      fontWeight: 800,
      letterSpacing: { xs: "-0.8px", sm: "-1.4px" },
      lineHeight: 1.1,
      fontVariantNumeric: "tabular-nums",
      minWidth: 0,
      overflowWrap: "anywhere",
      color: color || "var(--text-primary)",
    }}
  >
    {children}
  </Typography>
);

const Heroe = ({ dato, unidad, subtitulo, children, ...rest }) => (
  <Box {...rest} sx={{ pt: 0.75, pb: { xs: 1.25, sm: 1.75 } }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.1, flexWrap: "wrap" }}>
      {dato}
      {unidad && (
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)" }}>
          {unidad}
        </Typography>
      )}
    </Box>
    <Typography sx={{ fontSize: 12.5, color: "var(--text-muted)", mt: 0.625 }}>
      {subtitulo}
    </Typography>
    {children}
  </Box>
);

const NotaVacia = ({ children, ...rest }) => (
  <Typography {...rest} sx={{ fontSize: 12.5, color: "var(--text-faint)", py: 1.5, lineHeight: 1.55 }}>
    {children}
  </Typography>
);

const HistorialDePagos = () => {
  const pagos = useMyPayments();
  // null = falló o todavía no llegó: no se dibuja nada, ni un error.
  if (pagos === null) return null;
  if (pagos.length === 0) {
    return <NotaVacia>Historial de pagos: todavía no hay ninguno.</NotaVacia>;
  }
  const celdaEnc = {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    color: "var(--text-faint)",
    fontWeight: 700,
  };
  const columnas = { xs: "1fr auto", sm: "1.4fr 1fr 0.8fr 1.2fr" };
  return (
    <Box data-testid="mi-suscripcion-historial-pagos" sx={{ mt: 2 }}>
      <Typography
        variant="overline"
        sx={{ color: "rgba(var(--fg-rgb),0.6)", letterSpacing: 0.8, mb: 1, display: "block", fontWeight: 600 }}
      >
        Historial de pagos
      </Typography>
      <Box
        sx={{
          display: { xs: "none", sm: "grid" },
          gridTemplateColumns: columnas,
          gap: 1,
          pb: 0.875,
          borderBottom: "1px solid var(--border-soft)",
        }}
      >
        <Box sx={celdaEnc}>Período</Box>
        <Box sx={celdaEnc}>Monto</Box>
        <Box sx={celdaEnc}>Pagado</Box>
        <Box sx={celdaEnc}>Documento</Box>
      </Box>
      {pagos.map((p, i) => {
        const documento = p.dte_folio
          ? `${capitalizar(p.dte_tipo) || "Documento"} ${p.dte_folio}`
          : "Sin documento";
        return (
          <Box
            key={`${p.periodo}-${p.concepto}-${i}`}
            data-testid="mi-suscripcion-pago"
            sx={{
              display: "grid",
              gridTemplateColumns: columnas,
              gap: 1,
              py: 1,
              alignItems: "baseline",
              fontSize: 12,
              color: "var(--text-secondary)",
              fontVariantNumeric: "tabular-nums",
              borderBottom: "1px dashed var(--border-soft)",
              "&:last-of-type": { borderBottom: 0 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {periodoLegible(p.periodo)}
              <Box sx={{ display: { xs: "block", sm: "none" }, fontSize: 11, color: "var(--text-faint)", mt: 0.25 }}>
                {documento} · pagado {diaMesCorto(p.fecha_pago)}
              </Box>
            </Box>
            <Box sx={{ fontWeight: 600, color: "var(--text-primary)", textAlign: { xs: "right", sm: "left" } }}>
              {formatoMontoConMoneda(p.monto, p.moneda)}
            </Box>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>{diaMesCorto(p.fecha_pago)}</Box>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>{documento}</Box>
          </Box>
        );
      })}
    </Box>
  );
};

// En el popup de Ajustes (#1521) el panel vive dentro del diálogo, que ya es
// la superficie: sin card propia para no dibujar una caja dentro de otra.
const SIN_MARCO = { backgroundColor: "transparent", border: 0, borderRadius: 0, p: 0 };

// "Cambiar tarjeta" de una suscripción Paddle (#1680, R33; la cancelación salió al botón
// «Cancelar al final del período» de abajo, R52-R54): el portal
// de cliente de Paddle vive en otro dominio y la URL se pide al backend. La
// pestaña se abre ANTES de la espera: Safari bloquea window.open después de un
// await, y un bloqueo de popups dejaría el botón sin hacer nada.
const BotonPortalPaddle = () => {
  const [abriendo, setAbriendo] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const abrir = async () => {
    setAbriendo(true);
    setMensaje("");
    const pestana = window.open("", "_blank");
    try {
      const res = await getClient().get("/api/billing/me/portal/");
      const url = res?.data?.url;
      if (!url) throw new Error("sin_url");
      if (pestana) {
        pestana.opener = null;
        pestana.location.href = url;
      } else {
        window.open(url, "_blank", "noopener");
      }
    } catch (err) {
      if (pestana) pestana.close();
      setMensaje(
        err?.response?.data?.code === "sin_portal"
          ? `Por ahora no podemos abrir el portal de pagos. Escríbenos a ${SOPORTE} y te ayudamos a cambiar la tarjeta.`
          : `No pudimos abrir el portal de pagos. Intenta de nuevo o escríbenos a ${SOPORTE}.`
      );
    } finally {
      setAbriendo(false);
    }
  };
  return (
    <Box sx={{ mb: 1.75 }}>
      <Button
        data-testid="mi-suscripcion-portal"
        variant="outlined"
        size="small"
        disabled={abriendo}
        onClick={abrir}
        sx={botonGhostSx}
      >
        Cambiar tarjeta
      </Button>
      {mensaje && (
        <Typography
          data-testid="mi-suscripcion-portal-mensaje"
          role="alert"
          sx={{ fontSize: 12, color: "var(--text-muted)", mt: 0.75 }}
        >
          {mensaje}
        </Typography>
      )}
    </Box>
  );
};

// Fecha corta para la píldora: "31 oct 2026".
const formatoFechaCorta = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`)
      .toLocaleDateString("es-CL", { day: "numeric", month: "short", year: "numeric" })
      .replace(/\./g, "");
  } catch {
    return iso;
  }
};

// «Cancelar al final del período» desde Mi suscripción (#1680 R54). Cubre R54 —
// .claude/specs/1680-cobro-internacional/. Misma caja del mockup S2 (tools/mockups/
// smartdx_mockups_pago_chile_internacional.html). Sirve para las tres pasarelas: el backend
// (`POST /api/billing/me/subscription/cancel/`) decide qué hacer en cada una y solo marca si la
// pasarela confirmó; si no, 502/409 y acá un mensaje con el contacto, sin cambiar nada.
const CancelarAlFinal = ({ periodEnd }) => {
  const { refreshUser } = useCurrentUser();
  const [confirmando, setConfirmando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState("");
  const confirmar = async () => {
    setTrabajando(true);
    setError("");
    try {
      await postCancelarSuscripcion();
      try {
        await refreshUser();
      } catch (_) {
        // El servidor ya canceló; si /me no refresca, el panel se actualiza en la próxima carga.
      }
    } catch (e) {
      // 409 `cancelar_no_disponible`: reintentar no lo arregla, esa suscripción se cancela con soporte.
      setError(
        e?.response?.status === 409
          ? `Esta suscripción no se puede cancelar desde la app. Escríbenos a ${SOPORTE} y la cancelamos contigo.`
          : `No pudimos cancelar tu suscripción. No se cambió nada: intenta de nuevo en un rato o escríbenos a ${SOPORTE}.`
      );
      setConfirmando(false);
    } finally {
      setTrabajando(false);
    }
  };
  return (
    <Box sx={{ mb: 1.75 }}>
      <Button
        data-testid="suscripcion-cancelar"
        variant="outlined"
        size="small"
        onClick={() => {
          setError("");
          setConfirmando(true);
        }}
        sx={{
          ...botonGhostSx,
          color: "var(--color-danger)",
          borderColor: "rgba(var(--color-danger-rgb),0.4)",
          "&:hover": { backgroundColor: "rgba(var(--color-danger-rgb),0.08)", borderColor: "rgba(var(--color-danger-rgb),0.4)" },
        }}
      >
        Cancelar al final del período
      </Button>
      {error && (
        <Typography
          data-testid="suscripcion-cancelar-error"
          role="alert"
          sx={{ fontSize: 12, fontWeight: 600, color: "var(--color-danger)", mt: 0.75 }}
        >
          {error}
        </Typography>
      )}
      {confirmando && (
        <Box
          data-testid="suscripcion-cancelar-caja"
          role="group"
          sx={{
            mt: 1.5,
            p: "12px 13px",
            borderRadius: "12px",
            backgroundColor: "rgba(var(--color-danger-rgb),0.08)",
            border: "1px solid rgba(var(--color-danger-rgb),0.32)",
            fontSize: 12.8,
            color: "var(--text-tertiary)",
          }}
        >
          <Box component="b" sx={{ color: "var(--text-primary)" }}>
            Cancelar al final del período?
          </Box>
          <br />
          Seguirás con acceso completo hasta el {formatoFechaLarga(periodEnd)}. No se hará otro cobro y tu
          trabajo guardado se queda en tu cuenta.
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.25 }}>
            <Button
              data-testid="suscripcion-cancelar-mantener"
              variant="outlined"
              size="small"
              disabled={trabajando}
              onClick={() => setConfirmando(false)}
              sx={botonGhostSx}
            >
              Mantener mi plan
            </Button>
            <Button
              data-testid="suscripcion-cancelar-confirmar"
              variant="outlined"
              size="small"
              disabled={trabajando}
              onClick={confirmar}
              sx={{
                ...botonGhostSx,
                color: "var(--color-danger)",
                borderColor: "rgba(var(--color-danger-rgb),0.4)",
                "&:hover": { backgroundColor: "rgba(var(--color-danger-rgb),0.08)", borderColor: "rgba(var(--color-danger-rgb),0.4)" },
              }}
            >
              Sí, cancelar al final del período
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// El diálogo de pago vive AQUÍ, fuera de las ramas del panel (#1680, R31): al
// confirmarse el pago el sondeo refresca `/me/`, el panel pasa de "prueba" a
// "suscrito" y, si el diálogo colgara de la rama de la prueba, se desmontaría
// justo antes de mostrar "Listo".
export const SuscripcionPanel = (props) => {
  const [checkoutAbierto, setCheckoutAbierto] = useState(false);
  const { refreshUser } = useCurrentUser();
  return (
    <>
      <SuscripcionPanelContenido
        {...props}
        onElegirPlan={() => setCheckoutAbierto(true)}
        onRevisar={() => refreshUser().catch(() => {})}
      />
      <CheckoutDialog open={checkoutAbierto} onClose={() => setCheckoutAbierto(false)} />
    </>
  );
};

const SuscripcionPanelContenido = ({
  suscripcion: suscripcionCruda,
  access,
  trialEndsAt,
  sinMarco = false,
  onElegirPlan,
  onRevisar,
}) => {
  // #1680: una suscripción `pendiente` (checkout iniciado, pago sin confirmar) no
  // es una suscripción para el médico: el acceso sigue siendo el de la prueba.
  const suscripcion = suscripcionCruda?.status === "pendiente" ? null : suscripcionCruda;
  const encabezado = (pildora) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: { xs: 0.75, sm: 2 } }}>
      <Typography
        variant="overline"
        sx={{ color: "rgba(var(--fg-rgb),0.6)", letterSpacing: 0.8, display: "block", fontWeight: 600 }}
      >
        Mi suscripción
      </Typography>
      {pildora}
    </Box>
  );
  // El uso semanal reusa la píldora expandida (R6): ninguna barra propia.
  const uso = (
    <Box sx={{ pt: 1.75, borderTop: "1px solid var(--border-soft)" }}>
      <WeeklyUsagePill variant="expanded" />
    </Box>
  );

  // ── Suscrito ───────────────────────────────────────────────────────────
  if (suscripcion) {
    const alDia = suscripcion.status === "activa";
    // #1680 R54: activa y ya marcada para no renovar = plan que termina en `period_end`.
    const seCancela = alDia && Boolean(suscripcion.cancel_at_period_end);
    const neto = Number(suscripcion.price_net);
    const iva = Number(suscripcion.iva_rate || 0);
    // #1680 R33: en USD (Paddle, impuesto del país calculado por Paddle) o con
    // `iva_rate` 0 no hay IVA chileno que mostrar: ni "IVA incluido" ni "+ IVA".
    const sinIva = suscripcion.currency === "USD" || iva === 0;
    const formatoHeroe = (monto) =>
      suscripcion.currency === "USD" ? formatoUsd(monto) : formatoMontoConMoneda(monto, suscripcion.currency);
    // #1809: en CLP el IVA va redondeado al peso con la regla del backend (`billing-monto`):
    // `neto * 1.19` mostraba "$59.899,84" a un médico al que Flow le cobra $59.900.
    const enClp = suscripcion.currency === "CLP";
    const montoConIva = formatoHeroe(enClp ? montoBrutoSugerido(neto, iva) : neto * (1 + iva));
    const montoNeto = formatoHeroe(neto);
    // El IVA como MONTO, no como porcentaje: es lo que el médico entiende sin
    // hacer la cuenta (revisor #862 F6).
    const montoDeIva = formatoHeroe(enClp ? montoIvaSugerido(neto, iva) : neto * iva);
    const plan = SUSCRIPCION_PLAN_LABELS[suscripcion.plan] || suscripcion.plan;
    const cohorte = SUSCRIPCION_COHORTE_CORTA[suscripcion.cohort] || suscripcion.cohort;
    return (
      <Panel data-testid="perfil-suscripcion" sx={sinMarco ? SIN_MARCO : undefined}>
        {encabezado(
          <PildoraEstado tono={alDia && !seCancela ? "ok" : "warn"}>
            {seCancela ? `Se cancela el ${formatoFechaCorta(suscripcion.period_end)}` : alDia ? "Al día" : "Cancelada"}
          </PildoraEstado>
        )}
        <Box data-testid="mi-suscripcion-suscrito">
          <Heroe
            dato={<DatoGrande data-testid="mi-suscripcion-monto">{montoConIva}</DatoGrande>}
            unidad={sinIva ? "al mes" : "al mes, IVA incluido"}
            subtitulo={`${sinIva ? "" : `${montoNeto} neto + ${montoDeIva} de IVA · `}${plan}, ${cohorte}.${
              alDia ? "" : " Escríbenos a sebastian@smardx.com si quieres reactivarla."
            }`}
          />
          {suscripcion.gateway === "paddle" && <BotonPortalPaddle />}
          <Box sx={{ display: "flex", gap: { xs: 2.5, sm: 2.75 }, flexWrap: "wrap", mt: 0.5, mb: 1.75, pt: 1.75, borderTop: "1px solid var(--border-soft)" }}>
            <Box>
              <Box sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-faint)", fontWeight: 700 }}>
                Pagado hasta
              </Box>
              <Box sx={{ fontSize: 13, fontWeight: 700, mt: 0.375 }}>
                {formatoFechaLarga(suscripcion.period_end)}
              </Box>
            </Box>
            {/* #1809: jornadas del plan; con un acuerdo por cuenta, «3 → 4 · acordado contigo». */}
            {suscripcion.jornadas !== undefined && (
              <Box data-testid="mi-suscripcion-jornadas">
                <Box sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-faint)", fontWeight: 700 }}>
                  Jornadas
                </Box>
                <Box sx={{ fontSize: 13, fontWeight: 700, mt: 0.375 }}>
                  <JornadasAlMes jornadas={suscripcion.jornadas} jornadasPlan={suscripcion.jornadas_plan} corto />
                </Box>
              </Box>
            )}
            {!seCancela && (
              <Box>
                <Box sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-faint)", fontWeight: 700 }}>
                  Se renueva
                </Box>
                <Box sx={{ fontSize: 13, fontWeight: 700, mt: 0.375 }}>
                  {diaSiguienteCorto(suscripcion.period_end)}
                </Box>
              </Box>
            )}
          </Box>
          {alDia && !seCancela && <CancelarAlFinal periodEnd={suscripcion.period_end} />}
          {seCancela && (
            <Typography
              data-testid="suscripcion-cancelada"
              sx={{ fontSize: 13, color: "var(--text-secondary)", mb: 1.75 }}
            >
              Tu plan termina el {formatoFechaLarga(suscripcion.period_end)}. No se hará otro cobro y tu
              trabajo guardado se queda en tu cuenta.
            </Typography>
          )}
        </Box>
        <SaldoSueltas />
        {uso}
        <HistorialDePagos />
      </Panel>
    );
  }

  // ── País sin cobro (#1680 R37): sigue gratis, sin elegir plan ──────────
  if (access?.estado === "gratis_pais") {
    return (
      <Panel data-testid="perfil-suscripcion" sx={sinMarco ? SIN_MARCO : undefined}>
        {encabezado(<PildoraEstado tono="info">Sin costo</PildoraEstado>)}
        <Heroe
          data-testid="mi-suscripcion-gratis-pais"
          dato={<DatoGrande palabra>Gratis</DatoGrande>}
          subtitulo={
            <>
              Por ahora no tenemos suscripción en tu país. Puedes seguir usando SmartDx sin costo.
              {/* #1681 D62: por WhatsApp, sin correo ni mailto. */}
              {" "}Si tienes dudas,{" "}
              <Box component="a" data-testid="perfil-gratis-whatsapp" href={WHATSAPP_SOPORTE} target="_blank" rel="noopener noreferrer" sx={{ color: "var(--color-info)" }}>
                escríbenos por WhatsApp
              </Box>
              .
            </>
          }
        />
        {uso}
        <NotaVacia>Historial de pagos: no hay pagos que mostrar.</NotaVacia>
      </Panel>
    );
  }

  // ── Pago pendiente (#1680): checkout iniciado o transferencia avisada ───
  if (suscripcionCruda?.status === "pendiente") {
    return (
      <Panel data-testid="perfil-suscripcion" sx={sinMarco ? SIN_MARCO : undefined}>
        {encabezado(<PildoraEstado tono="warn">Pendiente</PildoraEstado>)}
        <Heroe
          data-testid="mi-suscripcion-pendiente"
          dato={<DatoGrande palabra color="var(--color-warning)">Pendiente</DatoGrande>}
          subtitulo="Pago pendiente de confirmación. Apenas lo confirmemos, tu plan queda activo."
        />
        <Box sx={{ mb: 1.75, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            data-testid="mi-suscripcion-revisar"
            variant="outlined"
            onClick={onRevisar}
            sx={botonGhostSx}
          >
            Volver a revisar
          </Button>
          <Button
            data-testid="checkout-abrir"
            variant="contained"
            disableElevation
            onClick={onElegirPlan}
            sx={botonPrimarioSx}
          >
            Elegir plan
          </Button>
        </Box>
        {uso}
        <NotaVacia>Historial de pagos: todavía no hay ninguno.</NotaVacia>
      </Panel>
    );
  }

  // ── Prueba (demo) ──────────────────────────────────────────────────────
  const dias = access?.dias_restantes;
  if (access?.estado === "demo" && typeof dias === "number") {
    const porVencer = dias <= DIAS_PRUEBA_POR_VENCER;
    const color = porVencer ? "var(--color-warning)" : "var(--color-primary-soft)";
    const hasta = fechaLargaDeInstante(trialEndsAt);
    return (
      <Panel data-testid="perfil-suscripcion" sx={sinMarco ? SIN_MARCO : undefined}>
        {encabezado(
          <PildoraEstado tono={porVencer ? "warn" : "info"}>
            {porVencer ? `${dias === 1 ? "Queda 1 día" : `Quedan ${dias} días`}` : "En prueba"}
          </PildoraEstado>
        )}
        <Heroe
          dato={<DatoGrande color={color} data-testid="mi-suscripcion-demo-dias">{dias}</DatoGrande>}
          unidad={dias === 1 ? "día de prueba" : "días de prueba"}
          subtitulo={
            <>
              {hasta && (
                <>
                  Hasta el <span data-testid="mi-suscripcion-demo-hasta">{hasta}</span>.{" "}
                </>
              )}
              {porVencer
                ? "Elige un plan antes de que termine."
                : "Cuando quieras, puedes elegir un plan."}
            </>
          }
        />
        <Box sx={{ mb: 1.75 }}>
          <Button
            data-testid="checkout-abrir"
            variant="contained"
            disableElevation
            onClick={onElegirPlan}
            sx={botonPrimarioSx}
          >
            Elegir plan
          </Button>
        </Box>
        <SaldoSueltas />
        {uso}
        <NotaVacia>Historial de pagos: todavía no hay ninguno.</NotaVacia>
      </Panel>
    );
  }

  // ── Sin plan, con jornadas sueltas (#1887, R23): la prueba terminó pero la cuenta compró una suelta, así que sigue entrando a
  // leer, copiar, editar y guardar (`access.aviso === "suelta"`). Sin esta rama caía en «Acceso permanente», que no es verdad.
  if (access?.aviso === "suelta") {
    return (
      <Panel data-testid="perfil-suscripcion" sx={sinMarco ? SIN_MARCO : undefined}>
        {encabezado(<PildoraEstado tono="info">Sin plan</PildoraEstado>)}
        <Heroe
          data-testid="mi-suscripcion-sin-plan"
          dato={<DatoGrande palabra>Sin plan</DatoGrande>}
          subtitulo={<SubtituloSinPlan />}
        />
        <Box sx={{ mb: 1.75 }}>
          <Button data-testid="checkout-abrir" variant="contained" disableElevation onClick={onElegirPlan} sx={botonPrimarioSx}>
            Elegir plan
          </Button>
        </Box>
        <SaldoSueltas />
        {/* Sin «Historial de pagos: todavía no hay ninguno»: esta rama solo existe para quien YA pagó jornadas adicionales con Flow
            (`access.aviso === "suelta"`), así que esa frase siempre sería falsa (revisor del front, falla 3). */}
        {uso}
      </Panel>
    );
  }

  // ── Sin fecha: legacy, exenta, o `access` ausente (R5) ─────────────────
  // No es un error ni pide resolver nada: es una cuenta que no vence.
  return (
    <Panel data-testid="perfil-suscripcion" sx={sinMarco ? SIN_MARCO : undefined}>
      {encabezado(<PildoraEstado tono="info">Acceso permanente</PildoraEstado>)}
      <Heroe
        data-testid="mi-suscripcion-sin-fecha"
        dato={<DatoGrande palabra>Permanente</DatoGrande>}
        subtitulo="Sin fecha de vencimiento. Tu cuenta no tiene una suscripción de cobro asociada."
      />
      {uso}
      <NotaVacia>No hay pagos que mostrar: tu acceso se gestiona fuera de la app.</NotaVacia>
    </Panel>
  );
};

const Panel = ({ children, sx, ...rest }) => (
  <Box
    sx={{
      backgroundColor: "var(--bg-elevated)",
      borderRadius: 3,
      border: "1px solid rgba(var(--fg-rgb),0.08)",
      p: { xs: 2.5, sm: 3 },
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

// #1681 D55: solo se listan los módulos encendidos. Los apagados no se nombran
// (D10/D52: los módulos que no se venden no se nombran a quien no los tiene). D58: los nombres
// salen de HERRAMIENTAS, la misma lista de la barra.
export const ModulosHabilitadosPanel = ({ currentUser }) => {
  const enabledModules = modulosEncendidos(currentUser);
  return (
    <Panel>
      <Typography
        variant="overline"
        sx={{ color: "rgba(var(--fg-rgb),0.6)", letterSpacing: 0.8, mb: 1.5, display: "block" }}
      >
        Módulos habilitados
      </Typography>
      {enabledModules.length === 0 ? (
        <Typography variant="body2" sx={{ color: "rgba(var(--fg-rgb),0.55)" }}>
          Aún no tienes módulos habilitados. Se activan al aprobar tu verificación.
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {enabledModules.map((m) => (
            <Chip
              key={m.flag}
              label={`✓ ${m.label}`}
              sx={{
                backgroundColor: "rgba(74,222,128,0.12)",
                color: "var(--color-success)",
                border: "1px solid rgba(74,222,128,0.3)",
                fontWeight: 600,
              }}
            />
          ))}
        </Stack>
      )}
    </Panel>
  );
};


const getInitials = (name, email) => {
  const source = (name || email || "?").trim();
  const parts = source.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("");
};

const ProfileView = () => {
  const navigate = useNavigate();
  const { currentUser, loading, refreshUser } = useCurrentUser();
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profile, setProfile] = useState(currentUser?.professional_profile || null);

  useEffect(() => {
    setProfile(currentUser?.professional_profile || null);
  }, [currentUser]);

  if (loading || !currentUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const status = profile?.verification_status || "NOT_SUBMITTED";
  const conVerificacion = pideVerificacion(profile);
  const professionDef = PROFESSIONS.find((p) => p.value === profile?.profession);
  const fullName = profile?.full_name || currentUser.username || currentUser.email;
  const initials = getInitials(fullName, currentUser.email);


  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const payload = new FormData();
      payload.append("profile_photo", file);
      const response = await client.patch("/api/user/profile/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile(response.data);
    } catch (err) {
      console.error("No pudimos subir la foto", err);
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, pt: { xs: 8, sm: 10 }, pb: 6, maxWidth: 1100, mx: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
          gap: 3,
        }}
      >
        <Stack spacing={3}>
          <Panel>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              alignItems={{ xs: "center", sm: "flex-start" }}
            >
              <Box sx={{ position: "relative" }}>
                <Avatar
                  src={profile?.profile_photo || undefined}
                  sx={{
                    width: 96,
                    height: 96,
                    fontSize: 30,
                    fontWeight: 700,
                    background: "linear-gradient(135deg, var(--color-primary), var(--color-primary-soft))",
                  }}
                >
                  {initials}
                </Avatar>
                <Button
                  component="label"
                  size="small"
                  sx={{
                    position: "absolute",
                    bottom: -4,
                    right: -4,
                    minWidth: 0,
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: "var(--color-primary)",
                    "&:hover": { backgroundColor: "var(--color-primary-soft)" },
                  }}
                  disabled={uploadingPhoto}
                >
                  <PhotoCameraIcon sx={{ fontSize: 16, color: "var(--text-primary)" }} />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handlePhotoChange}
                  />
                </Button>
              </Box>

              <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "left" } }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  justifyContent={{ xs: "center", sm: "flex-start" }}
                  flexWrap="wrap"
                >
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {fullName}
                  </Typography>
                  {conVerificacion && <VerificationStatusBadge status={status} />}
                </Stack>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(var(--fg-rgb),0.65)", mt: 0.5 }}
                >
                  {professionDef?.label || "Profesión sin definir"}
                  {profile?.registration_number
                    ? ` · Reg. ${profile.registration_number}`
                    : ""}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(var(--fg-rgb),0.5)", display: "block", mt: 0.5 }}
                >
                  {currentUser.email}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate("/perfil/editar")}
                sx={{
                  color: "var(--text-primary)",
                  borderColor: "rgba(var(--fg-rgb),0.25)",
                  "&:hover": {
                    borderColor: "var(--color-primary)",
                    backgroundColor: "rgba(31,111,151,0.08)",
                  },
                }}
              >
                Editar
              </Button>
            </Stack>

            {/* #1604: la verificación es solo para Chile (pideVerificacion). */}
            {conVerificacion && (status === "NOT_SUBMITTED" || status === "REJECTED") && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor:
                    status === "REJECTED"
                      ? "rgba(248,113,113,0.08)"
                      : "rgba(251,191,36,0.08)",
                  border: `1px solid ${
                    status === "REJECTED"
                      ? "rgba(248,113,113,0.3)"
                      : "rgba(251,191,36,0.3)"
                  }`,
                }}
              >
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  {status === "REJECTED"
                    ? "Tu verificación fue rechazada"
                    : "Aún no completaste tu verificación"}
                </Typography>
                {status === "REJECTED" && profile?.verification_notes && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mb: 1.5,
                      color: "rgba(var(--fg-rgb),0.8)",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    Motivo: {profile.verification_notes}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate("/perfil/verificacion")}
                >
                  {status === "REJECTED" ? "Reenviar" : "Completar verificación"}
                </Button>
              </Box>
            )}
          </Panel>

          <SuscripcionPanel
            suscripcion={currentUser.suscripcion}
            access={currentUser.access}
            trialEndsAt={currentUser.trial_ends_at}
          />

          {/* #1683 R48: preferencias de comunicación (mockup 1A). */}
          <ComunicacionesPanel comunicaciones={currentUser.comunicaciones} onCambio={() => refreshUser && refreshUser().catch(() => {})} />

          <ModulosHabilitadosPanel currentUser={currentUser} />

          <Panel>
            <Typography
              variant="overline"
              sx={{ color: "rgba(var(--fg-rgb),0.6)", letterSpacing: 0.8, mb: 1, display: "block" }}
            >
              Bio
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(var(--fg-rgb),0.85)", whiteSpace: "pre-wrap" }}
            >
              {profile?.bio ||
                "Aún no agregaste una bio. Contá en qué trabajás y qué te interesa."}
            </Typography>
          </Panel>
        </Stack>

        <Stack spacing={3}>
          {conVerificacion && (
          <Panel>
            <Typography
              variant="overline"
              sx={{ color: "rgba(var(--fg-rgb),0.6)", letterSpacing: 0.8, mb: 1.5, display: "block" }}
            >
              Tu verificación
            </Typography>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: "rgba(var(--fg-rgb),0.7)" }}>
                  Estado
                </Typography>
                <VerificationStatusBadge status={status} />
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: "rgba(var(--fg-rgb),0.7)" }}>
                  Carnet subido
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {profile?.has_id_document ? "✓" : "—"}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ color: "rgba(var(--fg-rgb),0.7)" }}>
                  Certificado SIS
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {profile?.has_health_certificate ? "✓" : "—"}
                </Typography>
              </Stack>
              {profile?.verified_at && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: "rgba(var(--fg-rgb),0.7)" }}>
                    Aprobado
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {new Date(profile.verified_at).toLocaleDateString()}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Panel>
          )}

          <Panel>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Typography
                variant="overline"
                sx={{ color: "rgba(var(--fg-rgb),0.6)", letterSpacing: 0.8 }}
              >
                Colegas
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(130,177,255,0.9)",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => navigate("/comunidad")}
              >
                ver todos →
              </Typography>
            </Stack>
            <DirectoryPreview onNavigate={navigate} />
          </Panel>
        </Stack>
      </Box>
      {/* #1683 R51: zona de eliminación al final de la Cuenta, ancho completo (mockup 2A). */}
      <Box sx={{ mt: 3 }}>
        <EliminarCuentaZona currentUser={currentUser} refreshUser={refreshUser} />
      </Box>
    </Box>
  );
};

export default ProfileView;
